/**
 * Matches - Match management page
 *
 * Features:
 * - Outlook-style calendar with color coding and tooltips
 * - Match cards styled like Overview's photo cards
 * - MatchHub integration for lookup + manual entry
 * - IPTC template editor (Photo Mechanic style)
 * - FTP server assignment
 * - Photographer/Editor assignments with sign-off
 * - Role-based views (COMPANY_MANAGER, PLANNER)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  fetchAllMatches,
  fetchMatch,
  createMatch,
  updateMatch,
  deleteMatch,
  linkMatchHubFixture,
  fetchTeams,
  createTeam,
  fetchUsers,
  assignPhotographerToMatch,
  unassignPhotographerFromMatch,
  assignEditorToMatch,
  unassignEditorFromMatch,
  markMatchFullTime,
  undoMatchFullTime,
  fetchFtpTargetsForSelection,
  fetchGrades,
  fetchIptcTemplates,
  fetchCompanyIptcDefaults,
  fetchIptcPreview,
} from '../api';
import { usePermissions } from '../hooks';
import Spinner from '../shared/components/ui/Spinner';
import {
  Calendar,
  MatchCard,
  MatchModal,
  MatchDetails,
  MatchFilters,
} from '../features/matches/components';
import { getMatchStatus, isMatchOnDate } from '../features/matches/utils/dateUtils';
import { useSetTopBar } from '../layouts/ShellContext';
import { Mono, Btn, Kbd } from '../shared/components/redesign';
import styles from './Matches.module.css';

/**
 * Default filter state
 */
const DEFAULT_FILTERS = {
  timeRange: 'week',
  status: 'all',
  assignment: 'all',
  eventTypes: [],
  sport: null,
  search: '',
};

export default function Matches() {
  // Permissions
  const {
    canManageMatches,
    canConfirmLineup,
    canSetFullTime,
    canManagePersons,
    role,
    currentUserId,
  } = usePermissions();

  // Data state
  const [allMatches, setAllMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [editors, setEditors] = useState([]);
  const [ftpServers, setFtpServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  // Id of the match whose details (GET /matches/{id}/) the drawer holds. The
  // list row shown first has no FTP links, competition or IPTC config, and
  // an edit saved from it wrote blanks over all of them.
  const [detailsFor, setDetailsFor] = useState(null);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  // Filter state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Load initial data from API
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use capability-based filters instead of role-based
      // can_photograph: photographer, editor_photographer, company_manager
      // can_edit: editor, editor_photographer, company_manager
      // This allows the same user to appear in both lists (e.g., company_manager)
      const [matchesData, teamsData, photographersData, editorsData, ftpData] = await Promise.all([
        // All pages, not page 1 — the calendar must see every match, however old.
        fetchAllMatches().catch(() => []),
        fetchTeams({ limit: 500 }).catch(() => ({ results: [] })),
        fetchUsers({ can_photograph: 'true' }).catch(() => ({ results: [] })),
        fetchUsers({ can_edit: 'true' }).catch(() => ({ results: [] })),
        fetchFtpTargetsForSelection().catch(() => ({ defaults: [], available: [] })),
      ]);

      setAllMatches(matchesData.results || matchesData || []);
      setTeams(teamsData.results || teamsData || []);
      setPhotographers(photographersData.results || photographersData || []);
      setEditors(editorsData.results || editorsData || []);
      // Combine defaults and available FTP targets into a flat list for MatchDetails
      const allFtpTargets = [...(ftpData.defaults || []), ...(ftpData.available || [])];
      setFtpServers(allFtpTargets);
    } catch (err) {
      setError('Failed to load data');
      setAllMatches([]);
      setTeams([]);
      setPhotographers([]);
      setEditors([]);
      setFtpServers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Close details drawer on Escape (but let an open dialog handle it first)
  useEffect(() => {
    if (!selectedMatch) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"]')) return;
      setSelectedMatch(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedMatch]);

  // Close details drawer on click outside. Ignores match cards (they switch
  // matches instead of closing) and any portaled dialogs that render outside
  // the drawer tree.
  const detailsRef = useRef(null);
  useEffect(() => {
    if (!selectedMatch) return;
    const onDown = (e) => {
      if (detailsRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[data-match-card]')) return;
      if (e.target.closest?.('[role="dialog"]')) return;
      setSelectedMatch(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedMatch]);

  // Filter matches
  const filteredMatches = useMemo(() => {
    let result = allMatches;

    // Filter by selected date from calendar
    if (selectedDate) {
      result = result.filter((m) => isMatchOnDate(m, selectedDate));
    }

    // Time range filter
    if (!selectedDate && filters.timeRange !== 'all') {
      const now = new Date();
      result = result.filter((match) => {
        const matchDate = new Date(match.start_time);
        if (filters.timeRange === 'today') {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          return matchDate >= today && matchDate < tomorrow;
        } else if (filters.timeRange === 'week') {
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          weekStart.setHours(0, 0, 0, 0);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          return matchDate >= weekStart && matchDate < weekEnd;
        } else if (filters.timeRange === 'month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          return matchDate >= monthStart && matchDate < monthEnd;
        }
        return true;
      });
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter((m) => getMatchStatus(m) === filters.status);
    }

    // Assignment filter
    if (filters.assignment === 'mine' && currentUserId) {
      result = result.filter((m) => {
        const isAssigned =
          m.photographer_assignments?.some((a) => a.user_id === currentUserId) ||
          m.editor_assignments?.some((a) => a.user_id === currentUserId);
        return isAssigned;
      });
    } else if (filters.assignment === 'unassigned') {
      result = result.filter((m) => {
        const hasAssignments =
          (m.photographer_assignments?.length || 0) > 0 ||
          (m.editor_assignments?.length || 0) > 0;
        return !hasAssignments;
      });
    }

    // Event type filter
    if (filters.eventTypes.length > 0) {
      result = result.filter((m) => filters.eventTypes.includes(m.event_type));
    }

    // Sport filter
    if (filters.sport) {
      result = result.filter((m) => m.sport === filters.sport);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((m) => {
        const searchable = [
          m.name,
          m.home_team_name,
          m.away_team_name,
          m.venue,
          m.competition,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchable.includes(searchLower);
      });
    }

    // Sort: upcoming first, then live, then past
    result.sort((a, b) => {
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return aTime - bTime;
    });

    return result;
  }, [allMatches, selectedDate, filters, currentUserId]);

  // Get matches for a calendar date
  const getMatchesOnDate = useCallback(
    (date) => allMatches.filter((m) => isMatchOnDate(m, date)),
    [allMatches]
  );

  // Wire the redesign top-bar with filter context + keyboard hints + new-match
  // CTA. Source: design_handoff_tank/screens-matches.jsx — top-right shows
  // "/ search", "N new match" Kbd hints, and a phosphor "+ New match" button.
  const setTopBar = useSetTopBar();
  useEffect(() => {
    const rangeLabel =
      {
        today: 'Today',
        week: 'This week',
        month: 'This month',
        all: 'All',
      }[filters.timeRange] || 'All';
    const breadcrumb = selectedDate
      ? selectedDate.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : rangeLabel;
    const right = (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--rd-space-3)',
        }}
      >
        <Mono style={{ fontSize: 'var(--rd-type-eyebrow)' }}>
          {filteredMatches.length}{' '}
          {filteredMatches.length === 1 ? 'match' : 'matches'}
        </Mono>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--rd-color-text-faint)',
            fontFamily: 'var(--rd-font-mono)',
            fontSize: 'var(--rd-type-eyebrow)',
            letterSpacing: 'var(--rd-track-eyebrow)',
            textTransform: 'uppercase',
          }}
        >
          <Kbd>/</Kbd>
          search
        </span>
        {canManageMatches && (
          <>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--rd-color-text-faint)',
                fontFamily: 'var(--rd-font-mono)',
                fontSize: 'var(--rd-type-eyebrow)',
                letterSpacing: 'var(--rd-track-eyebrow)',
                textTransform: 'uppercase',
              }}
            >
              <Kbd>N</Kbd>
              new match
            </span>
            <Btn
              kind="primary"
              size="sm"
              onClick={() => {
                setEditingMatch(null);
                setShowMatchModal(true);
              }}
            >
              + New match
            </Btn>
          </>
        )}
      </span>
    );
    setTopBar({ breadcrumb, right });
    return () => setTopBar(null);
  }, [
    filters.timeRange,
    selectedDate,
    filteredMatches.length,
    canManageMatches,
    setTopBar,
  ]);

  // Keyboard shortcut: "/" focuses search, "N" opens New Match modal.
  // Skip when the user is already typing into a field or a dialog is open.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const inField =
        t?.tagName === 'INPUT' ||
        t?.tagName === 'TEXTAREA' ||
        t?.isContentEditable;
      if (inField) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === '/') {
        e.preventDefault();
        document
          .querySelector('input[placeholder="Search team or competition"]')
          ?.focus();
      } else if (e.key === 'n' || e.key === 'N') {
        if (!canManageMatches) return;
        e.preventDefault();
        setEditingMatch(null);
        setShowMatchModal(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canManageMatches]);

  // Clear filters
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSelectedDate(null);
  }, []);

  // Create/update match
  const handleSaveMatch = useCallback(
    async (matchData, matchHubLink) => {
      try {
        let savedMatchId;
        if (editingMatch) {
          await updateMatch(editingMatch.id, matchData);
          savedMatchId = editingMatch.id;
        } else {
          const created = await createMatch(matchData);
          savedMatchId = created.id;
        }
        // Apply a pending MatchHub fixture link (sets ids + syncs events).
        if (matchHubLink?.fixtureId && matchHubLink?.seasonId && savedMatchId) {
          try {
            const linkResult = await linkMatchHubFixture(
              savedMatchId,
              matchHubLink.fixtureId,
              matchHubLink.seasonId,
            );
            // Round 2: surface the soft duplicate-fixture warning as a non-blocking
            // info banner — two crews shooting the same fixture is legitimate, so
            // we never block, just let the planner know.
            if (linkResult?.warning === 'fixture_already_linked') {
              const others = (linkResult.linked_match_ids || []).join(', ');
              setError(
                `Match saved + linked. Note: MatchHub fixture is also linked to other match(es): ${others}.`,
              );
            }
          } catch (linkErr) {
            setError(linkErr.response?.data?.error || 'Match saved, but MatchHub link failed');
          }
        }
        setShowMatchModal(false);
        setEditingMatch(null);
        loadData();

        // Refresh selected match details if we were editing it
        if (savedMatchId && selectedMatch?.id === savedMatchId) {
          const updated = await fetchMatch(savedMatchId);
          setSelectedMatch(updated);
        }
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      }
    },
    [editingMatch, loadData, selectedMatch]
  );

  // Delete match
  const handleDeleteMatch = useCallback(async () => {
    if (!selectedMatch) return;
    if (!confirm('Delete this match?')) return;
    try {
      await deleteMatch(selectedMatch.id);
      setSelectedMatch(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [selectedMatch, loadData]);

  // Edit match
  const handleEditMatch = useCallback(() => {
    if (!selectedMatch) return;
    setEditingMatch(selectedMatch);
    setShowMatchModal(true);
  }, [selectedMatch]);

  // Assign photographer
  const handleAssignPhotographer = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await assignPhotographerToMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Unassign photographer
  const handleUnassignPhotographer = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await unassignPhotographerFromMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Assign editor
  const handleAssignEditor = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await assignEditorToMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Unassign editor
  const handleUnassignEditor = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await unassignEditorFromMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Assign FTP target
  const handleAssignFTP = useCallback(
    async (ftpTargetId) => {
      if (!selectedMatch) return;
      const current = (selectedMatch.ftp_target_assignments || []).map((a) => a.ftp_target_id);
      if (current.includes(ftpTargetId)) return;
      try {
        await updateMatch(selectedMatch.id, { ftp_target_ids: [...current, ftpTargetId] });
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Save IPTC template (inline edit in drawer)
  const handleSaveIPTC = useCallback(
    async (iptcConfig) => {
      if (!selectedMatch) return;
      try {
        await updateMatch(selectedMatch.id, { iptc_config: iptcConfig });
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Unassign FTP target
  const handleUnassignFTP = useCallback(
    async (ftpTargetId) => {
      if (!selectedMatch) return;
      const remaining = (selectedMatch.ftp_target_assignments || [])
        .map((a) => a.ftp_target_id)
        .filter((id) => id !== ftpTargetId);
      try {
        await updateMatch(selectedMatch.id, { ftp_target_ids: remaining });
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Create team
  // Takes a name, or { name, team_type, common_name } from the wizard's create
  // panel and the MatchHub pending card (which passed an object into the old
  // name-only signature and so created a team named "[object Object]").
  const handleCreateTeam = useCallback(
    async (arg) => {
      const { name, team_type: teamType = 'club', common_name: commonName = '' } =
        typeof arg === 'string' ? { name: arg } : arg || {};
      try {
        const team = await createTeam(name, teamType, commonName);
        const teamsData = await fetchTeams({ limit: 500 });
        setTeams(teamsData.results || teamsData || []);
        return team;
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    []
  );

  // Mark full time
  const handleMarkFullTime = useCallback(async () => {
    if (!selectedMatch) return;
    try {
      await markMatchFullTime(selectedMatch.id);
      const updated = await fetchMatch(selectedMatch.id);
      setSelectedMatch(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [selectedMatch]);

  // Undo full time
  const handleUndoFullTime = useCallback(async () => {
    if (!selectedMatch) return;
    try {
      await undoMatchFullTime(selectedMatch.id);
      const updated = await fetchMatch(selectedMatch.id);
      setSelectedMatch(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [selectedMatch]);

  // Lineup scan changed (retract / resolve-row from the XI tab): refresh the
  // selected match so the serializer's lineup_scan summary (the green XI
  // state) and the list stay in sync with the scan resource the tab renders.
  const handleLineupChanged = useCallback(async () => {
    if (!selectedMatch) return;
    try {
      const updated = await fetchMatch(selectedMatch.id);
      setSelectedMatch(updated);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [selectedMatch, loadData]);

  // Date selection from calendar
  const handleDateSelect = useCallback((date) => {
    setSelectedDate((prev) => {
      // Toggle off if same date
      if (
        prev &&
        prev.getFullYear() === date.getFullYear() &&
        prev.getMonth() === date.getMonth() &&
        prev.getDate() === date.getDate()
      ) {
        return null;
      }
      return date;
    });
  }, []);

  // Match click - fetch full details including IPTC config and FTP targets
  const handleMatchClick = useCallback(async (match) => {
    // Show the list row at once for a responsive drawer, but nothing in it
    // can edit or save until the details are in.
    setSelectedMatch(match);
    setDetailsFor(null);
    try {
      const fullMatch = await fetchMatch(match.id);
      setSelectedMatch(fullMatch);
      setDetailsFor(match.id);
    } catch (err) {
      console.error('Failed to load match details:', err);
      setError(
        "Could not load this match's details. Editing is off until they load: click the match to try again."
      );
    }
  }, []);

  // Is a match live?
  const isLive = selectedMatch && getMatchStatus(selectedMatch) === 'live';

  if (loading && allMatches.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="lg" />
          <p>Loading matches...</p>
        </div>
      </div>
    );
  }

  // Page-header subtitle: "5 in this week · default sort by kick-off"
  const sectionLabel =
    {
      today: 'today',
      week: 'this week',
      month: 'this month',
      all: 'in all matches',
    }[filters.timeRange] || 'in all matches';
  const headerSubtitle = selectedDate
    ? `${filteredMatches.length} on ${selectedDate.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
      })} · default sort by kick-off`
    : `${filteredMatches.length} ${sectionLabel} · default sort by kick-off`;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Matches</h1>
          <p className={styles.headerSubtitle}>{headerSubtitle}</p>
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Main layout — narrow left column (calendar + filters), single-column
          list of match cards. Source: screens-matches.jsx § Screen03Matches. */}
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <Calendar
            matches={allMatches}
            currentMonth={currentMonth}
            onMonthChange={setCurrentMonth}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            getMatchesOnDate={getMatchesOnDate}
            expanded={calendarExpanded}
            onToggleExpand={() => setCalendarExpanded(!calendarExpanded)}
          />
          {selectedDate && (
            <button
              className={styles.clearDate}
              onClick={() => setSelectedDate(null)}
            >
              Clear date filter
            </button>
          )}
          <MatchFilters
            filters={filters}
            onChange={setFilters}
            onClear={handleClearFilters}
            role={role}
          />
        </aside>

        {/* Match list */}
        <main className={styles.main}>
          {/* Section header — "THIS WEEK" eyebrow + thin divider + count.
              Source: screens-matches.jsx § Screen03Matches list header. */}
          <div className={styles.sectionHeader}>
            <Mono className={styles.sectionEyebrow}>
              {selectedDate
                ? selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })
                : (
                    {
                      today: 'Today',
                      week: 'This week',
                      month: 'This month',
                      all: 'All matches',
                    }[filters.timeRange] || 'All matches'
                  )}
            </Mono>
            <span className={styles.sectionDivider} />
            <Mono className={styles.sectionCount}>
              {filteredMatches.length}{' '}
              {filteredMatches.length === 1 ? 'match' : 'matches'}
            </Mono>
          </div>

          {filteredMatches.length === 0 ? (
            <div className={styles.empty}>
              {allMatches.length === 0
                ? 'No matches yet. Create one to get started.'
                : 'No matches match the current filters.'}
            </div>
          ) : (
            <div className={styles.matchList}>
              {filteredMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  selected={selectedMatch?.id === match.id}
                  onClick={() => handleMatchClick(match)}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Drawer A — slide-in 520 px overlay from the right with backdrop dim.
          Source: design_handoff_tank/screens-matches.jsx § Screen03MatchesDrawerA. */}
      {selectedMatch && (
        <>
          <div
            className={styles.drawerBackdrop}
            onClick={() => setSelectedMatch(null)}
            aria-hidden="true"
          />
          <aside
            className={styles.drawerPanel}
            ref={detailsRef}
            role="dialog"
            aria-label="Match details"
          >
            <MatchDetails
              match={selectedMatch}
              detailsLoaded={detailsFor === selectedMatch.id}
              photographers={photographers}
              editors={editors}
              ftpServers={ftpServers}
              onEdit={handleEditMatch}
              onDelete={handleDeleteMatch}
              onClose={() => setSelectedMatch(null)}
              onAssignPhotographer={handleAssignPhotographer}
              onUnassignPhotographer={handleUnassignPhotographer}
              onAssignEditor={handleAssignEditor}
              onUnassignEditor={handleUnassignEditor}
              onAssignFTP={handleAssignFTP}
              onUnassignFTP={handleUnassignFTP}
              onSaveIPTC={handleSaveIPTC}
              onMarkFullTime={handleMarkFullTime}
              onUndoFullTime={handleUndoFullTime}
              onLineupChanged={handleLineupChanged}
              canManage={canManageMatches}
              canConfirmLineup={canConfirmLineup}
              canSetFullTime={canSetFullTime}
              canManagePersons={canManagePersons}
              isLive={isLive}
            />
          </aside>
        </>
      )}

      {/* Match Modal */}
      {showMatchModal && (
        <MatchModal
          isOpen={true}
          match={editingMatch}
          teams={teams}
          photographers={photographers}
          editors={editors}
          onSave={handleSaveMatch}
          onClose={() => {
            setShowMatchModal(false);
            setEditingMatch(null);
          }}
          onCreateTeam={handleCreateTeam}
          fetchFtpTargets={fetchFtpTargetsForSelection}
          fetchGrades={fetchGrades}
          fetchIptcTemplates={fetchIptcTemplates}
          fetchCompanyDefaults={fetchCompanyIptcDefaults}
          fetchIptcPreview={fetchIptcPreview}
        />
      )}
    </div>
  );
}
